import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useToggleState } from '@hotelplan/libs.hooks-react';
import { sx2CssThemeFn } from '@hotelplan/libs.sx';
import { IBd4Props } from 'components/domain/bd4';
import { TFilterOptionName } from 'components/domain/filters/Filters.types';
import SearchFilters from 'components/domain/filters/SearchFilters';
import { ORLSortingContextProvider } from 'components/domain/orl/orlResults/ORLSorting';
import useORLCheckout from 'components/domain/orl/useORLCheckout';
import SearchControlForm from 'components/domain/search-control/components/SearchControlForm/SearchControlForm';
import CurrentSearchContextProvider from 'components/domain/srl/CurrentSearchContext';
import {
  FilterButton,
  SrlMobileOverlayWrapper,
} from 'components/domain/srl/SRL.common';
import SRLMobileCurrentSearch from 'components/domain/srl/SRLMobileCurrentSearch';
import PdpOrlContainer from './PdpOrlContainer';
import usePdpStaticComponents from './usePdpStaticComponents';

const OrlMobileCurrentSearch = styled(SRLMobileCurrentSearch)(
  sx2CssThemeFn({
    borderBottom: '1px solid',
    borderColor: 'borderGray',
    pt: 2,
  })
);

const OrlFilterButton = styled(FilterButton)(
  sx2CssThemeFn({
    ml: 'auto',
    mb: 2,
    mr: 2,
  })
);

const VerticalBd4TravelRecommendation = dynamic<IBd4Props>(
  () =>
    import('components/domain/bd4').then(
      m => m.VerticalBd4TravelRecommendation
    ),
  { ssr: false }
);

interface IPdpOrl {
  productName?: string;
}

const PdpOrl = ({ productName }: IPdpOrl) => {
  const [t] = useTranslation('filters');

  const [mobileOverlayOpened, open, close] = useToggleState(false);
  const { mobile } = useDeviceType();

  const orlCheckout = useORLCheckout(productName);
  const { bd4Group, loading } = usePdpStaticComponents();

  const displayingFilters: Array<TFilterOptionName> = [
    'departureAirports',
    'maxPrice',
    'roomTypes',
    'boardTypes',
    'flightOptions',
  ];

  return (
    <CurrentSearchContextProvider>
      <ORLSortingContextProvider>
        <PdpOrlContainer
          bd4Group={bd4Group}
          bd4GroupLoading={loading}
          productName={productName}
          orlCheckout={orlCheckout}
        >
          {!mobile ? (
            <>
              <SearchControlForm
                hideTravelDestination={true}
                filtersComponent={
                  <SearchFilters
                    includedOptions={displayingFilters}
                    showSubAmenities={false}
                  />
                }
              />
              <VerticalBd4TravelRecommendation
                name={bd4Group?.name}
                items={bd4Group?.items}
                tracking={bd4Group?.tracking}
                loading={loading}
              />
            </>
          ) : (
            <>
              <OrlFilterButton onClick={open}>
                {t('filter.overlay.title')}
              </OrlFilterButton>
              <OrlMobileCurrentSearch
                excludedFields={['travelDestination', 'productFeatures']}
              />
              <SrlMobileOverlayWrapper
                name={'orl-srl-mobile-overlay'}
                title={t('filter.overlay.title')}
                show={mobileOverlayOpened}
                fullScreen={true}
                onClose={close}
              >
                <SearchControlForm
                  closeMobileOverlay={close}
                  hideTravelDestination={true}
                  filtersComponent={
                    <SearchFilters
                      includedOptions={displayingFilters}
                      showSubAmenities={false}
                    />
                  }
                />
              </SrlMobileOverlayWrapper>
            </>
          )}
        </PdpOrlContainer>
      </ORLSortingContextProvider>
    </CurrentSearchContextProvider>
  );
};

export default PdpOrl;
